import React, {useState} from "react";
import classnames from "classnames";
import css from "./css/StudentSurveyTimingStep.scss";
import commonCss from "./css/SurveyStyle.scss";
import HourlyDropdown from "common/HourlyDropdown";
import Icon from "SF/MaterialIcon";
import {fifteenMinuteIntervalSlotsForDay} from "utils/time";
import PropTypes from "prop-types";
import {SingleDatePicker} from "react-dates";
import Moment from "moment";
import {getWord} from "utils/tenant";

function StudentSurveyTimingStep(props) {
    const [endDateFocused, setEndDateFocused] = useState(false);
    const [startDateFocused, setStartDateFocused] = useState(false);

    const availableTimeSlotOptions = fifteenMinuteIntervalSlotsForDay(props.abbreviatedTZ);

    const filterByLabel = (option, input) => {
        return option.label.includes(input);
    }

    const openTimeDropdown = () =>
        <label className={css['label-text']} htmlFor={"open-time"}>
            <span className={commonCss['required-asterisk']} aria-hidden>*</span><span>Open Time</span>
            <HourlyDropdown id="open-time"
                            ariaLabel="Select Open Time"
                            options={availableTimeSlotOptions}
                            placeholder="Open Time"
                            filterOption={filterByLabel}
                            className={css['timeSlotDropdown']}
                            onChange={props.setOpenTime}
                            value={props.openTime}
                            isDisabled={!props.openDateTimeEditable} />
        </label>;

    const closeTimeDropdown = () =>
        <label className={css['label-text']} htmlFor={"close-time"}>
            <span>Close Time</span>
            <HourlyDropdown id="close-time"
                            ariaLabel="Select Close Time"
                            options = {availableTimeSlotOptions}
                            placeholder="Close Time"
                            filterOption={filterByLabel}
                            className={css['timeSlotDropdown']}
                            onChange={props.setCloseTime}
                            value={props.closeTime}
                            isDisabled={!props.closeDateTimeEditable} />
        </label>;

    const requiredOpenDateError = () =>
        <tr>
            <td colSpan={2} className={css['error']}>
                <span className={commonCss['field-error']} role='alert'>
                    <Icon icon='error' aria-hidden />Open date is required after the {getWord('studentSurvey', false)} is scheduled.
                </span>
            </td>
        </tr>

    const startDate = () =>
        <>
            <div className={css['label-text']} >
                <span className={commonCss['required-asterisk']}>*</span>
                <label htmlFor="start-date-select">Open Date</label>
            </div>
            <div className={css['date-select']}>
                <SingleDatePicker
                    date={props.openDate}
                    onDateChange={props.setOpenDate}
                    focused={startDateFocused}
                    onFocusChange={({ focused }) => setStartDateFocused(focused)}
                    id="start-date-select"
                    numberOfMonths={1}
                    displayFormat="MM-DD-YYYY"
                    placeholder="MM-DD-YYYY"
                    hideKeyboardShortcutsPanel
                    showClearDate={props.openDateTimeEditable}
                    showDefaultInputIcon={props.openDateTimeEditable}
                    customInputIcon={!props.openDateTimeEditable && <Icon icon='lock' aria-hidden />}
                    inputIconPosition="after"
                    openDirection="down"
                    disabled={!props.openDateTimeEditable} />
            </div>
        </>

    const endDate = () =>
        <>
            <div className={css['label-text']}>
                <label htmlFor="end-date-select">Close Date</label>
                <div className={css['date-select']}>
                    <SingleDatePicker
                        date={props.closeDate}
                        onDateChange={props.setCloseDate}
                        focused={endDateFocused}
                        onFocusChange={({ focused }) => setEndDateFocused(focused)}
                        id="end-date-select"
                        numberOfMonths={1}
                        displayFormat="MM-DD-YYYY"
                        placeholder="MM-DD-YYYY"
                        isOutsideRange={date => date.isBefore(props.openDate)}
                        hideKeyboardShortcutsPanel
                        showClearDate={props.closeDateTimeEditable}
                        showDefaultInputIcon={props.closeDateTimeEditable}
                        customInputIcon={!props.closeDateTimeEditable && <Icon icon='lock' aria-hidden />}
                        inputIconPosition="after"
                        openDirection="down"
                        disabled={!props.closeDateTimeEditable} />
                </div>
            </div>
        </>

    return (
        <div className={css['survey-schedule']}>
            <h2 className={commonCss['header-second-level']}>Timing</h2>
            <div className={classnames(commonCss['instructional-text'], commonCss['spacing-below'])}>
                This page cannot be modified after this {getWord('studentSurvey', false)} is scheduled AND the date/time has passed.
                <br/> <br/>
                When would you like to allow students to submit & edit their response? Students will be able to edit
                their responses within the time frame if they have the edit permission on the Properties page.
                If you do not populate a Close Date and Close Time then the survey will remain open indefinitely.
                All Time selections will be in your tenant’s time zone: <span className={commonCss['bold']}>{props.timezone}.</span>
            </div>
            <div className={commonCss['instructional-text']}>
                <span className={commonCss['required-asterisk']} aria-hidden>*</span>Required Fields
            </div>
            <table className={css['table']}>
                <tbody>
                    <tr>
                        <td className={classnames(css['dropdown'], css['date'])}>{startDate()}</td>
                        <td className={css['dropdown']}>{openTimeDropdown()}</td>
                    </tr>
                    {!props.openDateClearable && !props.openDate && requiredOpenDateError()}
                    <tr>
                        <td className={classnames(css['dropdown'], css['date'])}>{endDate()}</td>
                        <td className={css['dropdown']}>{closeTimeDropdown()}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    )
}

StudentSurveyTimingStep.propTypes = {
    abbreviatedTZ: PropTypes.string.isRequired,
    timezone: PropTypes.string.isRequired,
    openDate: PropTypes.instanceOf(Moment),
    closeDate: PropTypes.instanceOf(Moment),
    openTime: PropTypes.string,
    closeTime: PropTypes.string,
    setOpenDate: PropTypes.func.isRequired,
    setCloseDate: PropTypes.func.isRequired,
    setOpenTime: PropTypes.func.isRequired,
    setCloseTime: PropTypes.func.isRequired,
    openDateClearable: PropTypes.bool.isRequired,
    openDateTimeEditable: PropTypes.bool.isRequired,
    closeDateTimeEditable: PropTypes.bool.isRequired
}

export default StudentSurveyTimingStep;
